import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserSubscriptionInfo {
  hasActiveBusinessSubscriptions: boolean;
  businessesWithActiveSubscriptions: Array<{
    id: string;
    name: string;
    subscriptionStatus: string;
    subscriptionEndDate: string | null;
    isInGracePeriod: boolean;
  }>;
  canDeleteAccount: boolean;
  hasBusinessesInGracePeriod: boolean;
  communityCount: number;
  communities: Array<{
    id: string;
    name: string;
  }>;
  belongsToMultipleCommunities: boolean;
}

export function useUserSubscriptionStatus() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-subscription-status', user?.id],
    queryFn: async (): Promise<UserSubscriptionInfo> => {
      if (!user) {
        return {
          hasActiveBusinessSubscriptions: false,
          businessesWithActiveSubscriptions: [],
          canDeleteAccount: true,
          hasBusinessesInGracePeriod: false,
          communityCount: 0,
          communities: [],
          belongsToMultipleCommunities: false
        };
      }

      // First, check how many communities the user belongs to
      const { data: communitiesData, error: communitiesError } = await supabase
        .from('community_members')
        .select(`
          community_id,
          communities(id, name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'ACTIVE');

      if (communitiesError) {
        console.error('Error fetching user communities:', communitiesError);
        throw communitiesError;
      }

      const communities = communitiesData?.map(cm => ({
        id: cm.communities?.id || '',
        name: cm.communities?.name || ''
      })) || [];

      const communityCount = communities.length;
      const belongsToMultipleCommunities = communityCount > 1;

      // If user belongs to multiple communities, they cannot delete their account
      if (belongsToMultipleCommunities) {
        return {
          hasActiveBusinessSubscriptions: false,
          businessesWithActiveSubscriptions: [],
          canDeleteAccount: false,
          hasBusinessesInGracePeriod: false,
          communityCount,
          communities,
          belongsToMultipleCommunities
        };
      }

      // Get all businesses owned by the user with their subscription status
      const { data: businessesData, error } = await supabase
        .from('businesses')
        .select(`
          id,
          name,
          business_subscriptions(
            status,
            current_period_end,
            cancel_at_period_end,
            plan_id,
            community_subscription_plans(name)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!businessesData || businessesData.length === 0) {
        return {
          hasActiveBusinessSubscriptions: false,
          businessesWithActiveSubscriptions: [],
          canDeleteAccount: true,
          hasBusinessesInGracePeriod: false,
          communityCount,
          communities,
          belongsToMultipleCommunities
        };
      }

      const now = new Date();
      const businessesWithActiveSubscriptions = [];
      let hasBusinessesInGracePeriod = false;

      for (const business of businessesData) {
        // Get the latest subscription for this business
        const subscription = business.business_subscriptions?.[0];
        
        if (subscription) {
          const periodEnd = new Date(subscription.current_period_end);
          
          // Use the same logic as the database trigger: prevent_business_deletion_with_active_subscription
          // Block deletion if subscription status is active/trialing/past_due OR
          // if period hasn't ended and status is not canceled/unpaid/incomplete_expired
          const hasActiveSubscription = (
            ['active', 'trialing', 'past_due'].includes(subscription.status) ||
            (periodEnd > now && !['canceled', 'unpaid', 'incomplete_expired'].includes(subscription.status))
          );

          // Determine if we're in a grace period (canceled but still active until period end)
          const isInGracePeriod = subscription.status === 'canceled' && periodEnd > now;

          if (hasActiveSubscription) {
            businessesWithActiveSubscriptions.push({
              id: business.id,
              name: business.name,
              subscriptionStatus: subscription.status,
              subscriptionEndDate: subscription.current_period_end,
              isInGracePeriod
            });

            if (isInGracePeriod) {
              hasBusinessesInGracePeriod = true;
            }
          }
        }
      }

      const hasActiveBusinessSubscriptions = businessesWithActiveSubscriptions.length > 0;
      // User can delete account if they have no active subscriptions (following database trigger logic)
      // Canceled subscriptions are allowed to be deleted regardless of grace period
      const canDeleteAccount = !hasActiveBusinessSubscriptions;

      return {
        hasActiveBusinessSubscriptions,
        businessesWithActiveSubscriptions,
        canDeleteAccount,
        hasBusinessesInGracePeriod,
        communityCount,
        communities,
        belongsToMultipleCommunities
      };
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000 // 1 minute
  });
}